import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { 
  createBackup, 
  downloadBackup, 
  uploadBackup, 
  restoreBackup, 
  getBackupInfoFast,
  validateBackup 
} from '../../services/backupService';
import { getAllScreens, getAllCheckpoints } from '../../services/funnelService';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  background: ${theme.colors.neutral}05;
`;

const CardTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const CardDescription = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: ${theme.typography.lineHeights.normal};
`;

const Button = styled.button`
  background: ${props => 
    props.variant === 'danger' ? theme.colors.error :
    props.variant === 'secondary' ? 'white' :
    `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
  };
  color: ${props => props.variant === 'secondary' ? theme.colors.text : 'white'};
  border: 2px solid ${props => 
    props.variant === 'danger' ? theme.colors.error :
    props.variant === 'secondary' ? theme.colors.doryLighterBlue :
    'transparent'
  };
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  width: 100%;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
  background: ${theme.colors.doryBlue};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  display: block;
  text-align: center;
  
  &:hover {
    background: ${theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
`;

const StatusMessage = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  
  background: ${props => 
    props.type === 'success' ? `${theme.colors.success}20` :
    props.type === 'error' ? `${theme.colors.error}20` :
    props.type === 'warning' ? `${theme.colors.warning}20` :
    `${theme.colors.info}20`
  };
  
  border: 2px solid ${props => 
    props.type === 'success' ? theme.colors.success :
    props.type === 'error' ? theme.colors.error :
    props.type === 'warning' ? theme.colors.warning :
    theme.colors.info
  };
  
  color: ${props => 
    props.type === 'success' ? theme.colors.success :
    props.type === 'error' ? theme.colors.error :
    props.type === 'warning' ? theme.colors.warning :
    theme.colors.info
  };
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.neutral}20;
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  margin-top: ${theme.spacing.md};
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.sm};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const InfoItem = styled.div`
  text-align: center;
  padding: ${theme.spacing.sm};
  background: white;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.doryLighterBlue};
`;

const InfoValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.primary};
`;

const InfoLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.xs};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 90%;
  box-shadow: ${theme.shadows.lg};
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const ModalMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: ${theme.typography.lineHeights.normal};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin: ${theme.spacing.md} 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${theme.colors.primary};
`;

const CheckboxLabel = styled.label`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  cursor: pointer;
`;

const BackupManager = () => {
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreOptions, setRestoreOptions] = useState({
    clearExisting: false,
    clearUsers: false
  });
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  
  const fileInputRef = useRef(null);

  // Load backup info on component mount
  React.useEffect(() => {
    loadBackupInfo();
  }, []);

  const loadBackupInfo = async () => {
    try {
      // Use fast method initially to show counts quickly
      const info = await getBackupInfoFast();
      setBackupInfo(info);
    } catch (error) {
      console.error('Error loading backup info:', error);
      setStatus({ type: 'error', message: `Failed to load backup info: ${error.message}` });
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    setStatus(null);
    setProgress(0);
    
    try {
      setProgressText('Creating backup...');
      setProgress(25);
      
      const backup = await createBackup();
      
      setProgressText('Preparing download...');
      setProgress(75);
      
      await downloadBackup(backup);
      
      setProgress(100);
      setProgressText('Backup completed!');
      setStatus({ type: 'success', message: 'Backup created and downloaded successfully!' });
      
      // Refresh backup info
      await loadBackupInfo();
      
    } catch (error) {
      console.error('Error creating backup:', error);
      setStatus({ type: 'error', message: `Failed to create backup: ${error.message}` });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setProgressText('');
      }, 2000);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setStatus(null);
      
      const backup = await uploadBackup(file);
      
      // Validate the backup
      const validation = validateBackup(backup);
      setValidationResult(validation);
      
      if (validation.isValid) {
        setSelectedBackup(backup);
        setShowRestoreModal(true);
        setStatus({ type: 'success', message: 'Backup file loaded successfully! Ready to restore.' });
      } else {
        setStatus({ 
          type: 'error', 
          message: `Invalid backup file: ${validation.errors.join(', ')}` 
        });
      }
      
    } catch (error) {
      console.error('Error uploading backup:', error);
      setStatus({ type: 'error', message: `Failed to upload backup: ${error.message}` });
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;

    setLoading(true);
    setStatus(null);
    setProgress(0);
    setShowRestoreModal(false);

    try {
      await restoreBackup(selectedBackup, {
        clearExisting: restoreOptions.clearExisting,
        clearUsers: restoreOptions.clearUsers,
        onProgress: (text, percent) => {
          setProgressText(text);
          setProgress(percent);
        }
      });

      setProgress(100);
      setProgressText('Restore completed!');
      setStatus({ type: 'success', message: 'Backup restored successfully!' });
      
      // Refresh backup info
      await loadBackupInfo();
      
    } catch (error) {
      console.error('Error restoring backup:', error);
      setStatus({ type: 'error', message: `Failed to restore backup: ${error.message}` });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setProgressText('');
      }, 2000);
    }
  };

  const handleDryRun = async () => {
    if (!selectedBackup) return;

    try {
      setLoading(true);
      setStatus(null);
      
      const result = await restoreBackup(selectedBackup, { dryRun: true });
      
      setStatus({ 
        type: 'success', 
        message: `Dry run completed: ${result.summary.screensToRestore} screens, ${result.summary.checkpointsToRestore} checkpoints, ${result.summary.usersToRestore} users would be restored.` 
      });
      
    } catch (error) {
      console.error('Error in dry run:', error);
      setStatus({ type: 'error', message: `Dry run failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleExportMarkdown = async () => {
    try {
      setLoading(true);
      setStatus(null);
      
      // Fetch current data from Firebase
      const [screens, checkpoints] = await Promise.all([
        getAllScreens(),
        getAllCheckpoints()
      ]);
      
      const data = { screens, checkpoints };
      
      // Generate markdown content
      const markdown = generateMarkdown(data);
      
      // Download the file
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `funnel-structure-${timestamp}.md`;
      
      // Create and download the markdown file
      const dataBlob = new Blob([markdown], { type: 'text/markdown' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      
      setStatus({ 
        type: 'success', 
        message: `Markdown exported successfully! Downloaded ${filename} with ${Object.keys(screens).length} screens and ${Object.keys(checkpoints).length} checkpoints.` 
      });
      
    } catch (error) {
      console.error('Error exporting markdown:', error);
      setStatus({ type: 'error', message: `Failed to export markdown: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Function to format navigation pointer
  const formatNavigationPointer = (nextScreen) => {
    if (!nextScreen) return '**End of Funnel**';
    
    if (typeof nextScreen === 'string') {
      if (/^https?:\/\//i.test(nextScreen)) {
        return `External URL: [${nextScreen}](${nextScreen})`;
      }
      return `\`${nextScreen}\``;
    }
    
    if (typeof nextScreen === 'object' && nextScreen.conditions) {
      const conditions = Object.entries(nextScreen.conditions)
        .map(([value, screen]) => {
          const dest = /^https?:\/\//i.test(screen)
            ? `[${screen}](${screen})`
            : `\`${screen}\``;
          return `\`${value}\` → ${dest}`;
        })
        .join(', ');

      const defaultDest = nextScreen.default
        ? (/^https?:\/\//i.test(nextScreen.default)
            ? `[${nextScreen.default}](${nextScreen.default})`
            : `\`${nextScreen.default}\``)
        : 'none';
      
      return `Conditional: ${conditions} (default: ${defaultDest})`;
    }
    
    return '**Invalid Navigation**';
  };

  // Function to format content text
  const formatContentText = (content) => {
    if (!content) return '*No content*';
    
    let text = '';
    if (content.title) text += `**Title:** ${content.title}\n`;
    if (content.text) text += `**Description:** ${content.text}\n`;
    if (content.ctaText) text += `**CTA:** ${content.ctaText}`;
    
    return text.trim();
  };

  // Function to format questions and answers
  const formatQuestions = (questions) => {
    if (!questions || !Array.isArray(questions)) return '*No questions*';
    
    return questions.map((q, index) => {
      return `${index + 1}. **${q.text}**\n   - Type: ${q.type}\n   - Options: ${q.options}`;
    }).join('\n\n');
  };

  // Function to format options
  const formatOptions = (options) => {
    if (!options || !Array.isArray(options)) return '*No options*';
    
    return options.map((opt, index) => {
      return `${index + 1}. \`${opt.value}\` - ${opt.text}`;
    }).join('\n');
  };

  // Function to generate markdown content
  const generateMarkdown = (data) => {
    let markdown = `# OptimalPace Funnel Structure

*Generated on ${new Date().toLocaleString()}*

## Overview

This document shows the current structure of the OptimalPace marketing funnel, including all screens, checkpoints, their content, and navigation flow.

**Total Screens:** ${Object.keys(data.screens).length}  
**Total Checkpoints:** ${Object.keys(data.checkpoints).length}

---

## Screens

`;

    // Add screens section
    Object.entries(data.screens).forEach(([id, screen]) => {
      markdown += `### Screen: \`${screen.key}\`
- **ID:** ${screen.id}
- **Type:** ${screen.type}
- **Navigation:** ${formatNavigationPointer(screen.nextScreen)}

${formatContentText(screen.content)}

---
`;
    });

    markdown += `## Checkpoints

`;

    // Add checkpoints section
    Object.entries(data.checkpoints).forEach(([id, checkpoint]) => {
      markdown += `### Checkpoint: \`${checkpoint.key}\`
- **ID:** ${checkpoint.id}
- **Type:** ${checkpoint.type}
- **Navigation:** ${formatNavigationPointer(checkpoint.nextScreen)}

${formatContentText(checkpoint.content)}

`;

      // Add questions if they exist
      if (checkpoint.questions) {
        markdown += `**Questions:**
${formatQuestions(checkpoint.questions)}

`;
      }

      // Add options if they exist
      if (checkpoint.options) {
        markdown += `**Answer Options:**
${formatOptions(checkpoint.options)}

`;
      }

      markdown += `---
`;
    });

    // Add navigation flow diagram
    markdown += `## Navigation Flow

\`\`\`mermaid
graph TD
`;

    // Add nodes
    Object.entries(data.screens).forEach(([id, screen]) => {
      markdown += `    ${screen.key}[${screen.key}]`;
    });
    
    Object.entries(data.checkpoints).forEach(([id, checkpoint]) => {
      markdown += `    ${checkpoint.key}[${checkpoint.key}]`;
    });

    markdown += `
`;

    // Add edges
    const formatMermaidDest = (dest, sourceKey) => {
      if (/^https?:\/\//i.test(dest)) {
        const nodeId = `${sourceKey}_ext_url`;
        return { edge: `${sourceKey} --> ${nodeId}[External URL]`, nodeId };
      }
      return { edge: `${sourceKey} --> ${dest}`, nodeId: null };
    };

    Object.entries(data.screens).forEach(([id, screen]) => {
      if (screen.nextScreen) {
        if (typeof screen.nextScreen === 'string') {
          markdown += `    ${formatMermaidDest(screen.nextScreen, screen.key).edge}`;
        } else if (screen.nextScreen.default) {
          markdown += `    ${formatMermaidDest(screen.nextScreen.default, screen.key).edge}`;
        }
      }
    });
    
    Object.entries(data.checkpoints).forEach(([id, checkpoint]) => {
      if (checkpoint.nextScreen) {
        if (typeof checkpoint.nextScreen === 'string') {
          markdown += `    ${formatMermaidDest(checkpoint.nextScreen, checkpoint.key).edge}`;
        } else if (checkpoint.nextScreen.default) {
          markdown += `    ${formatMermaidDest(checkpoint.nextScreen.default, checkpoint.key).edge}`;
        }
      }
    });

    markdown += `
\`\`\`

---

## Data Export Information

- **Export Date:** ${new Date().toISOString()}
- **Data Source:** Current Firebase Data
- **Collections Exported:** screens, checkpoints
- **Total Items:** ${Object.keys(data.screens).length + Object.keys(data.checkpoints).length}

*This document was automatically generated from the current Firebase data.*
`;

    return markdown;
  };

  return (
    <Container>
      <SectionHeader>
        <SectionTitle>Backup & Restore</SectionTitle>
      </SectionHeader>

      <Grid>
        {/* Create Backup Card */}
        <Card>
          <CardTitle>Create Backup</CardTitle>
          <CardDescription>
            Create a complete backup of all screens, checkpoints, and user data structure. 
            The backup will be downloaded as a JSON file.
          </CardDescription>
          
          <Button 
            onClick={handleCreateBackup} 
            disabled={loading}
          >
            {loading ? 'Creating Backup...' : 'Create Backup'}
          </Button>

          <Button 
            onClick={handleExportMarkdown} 
            disabled={loading}
            variant="secondary"
            style={{ marginTop: '12px' }}
          >
            Export to Markdown
          </Button>

          {backupInfo && (
            <InfoGrid>
              <InfoItem>
                <InfoValue>{backupInfo.metadata.screensCount}</InfoValue>
                <InfoLabel>Screens</InfoLabel>
              </InfoItem>
              <InfoItem>
                <InfoValue>{backupInfo.metadata.checkpointsCount}</InfoValue>
                <InfoLabel>Checkpoints</InfoLabel>
              </InfoItem>
              <InfoItem>
                <InfoValue>{backupInfo.metadata.usersCount}</InfoValue>
                <InfoLabel>Users</InfoLabel>
              </InfoItem>
              <InfoItem>
                <InfoValue>{formatFileSize(backupInfo.estimatedSize)}</InfoValue>
                <InfoLabel>Size</InfoLabel>
              </InfoItem>
            </InfoGrid>
          )}
        </Card>

        {/* Restore Backup Card */}
        <Card>
          <CardTitle>Restore Backup</CardTitle>
          <CardDescription>
            Upload a backup file to restore your funnel data. 
            You can choose to clear existing data before restoration.
          </CardDescription>
          
          <FileInput
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            id="backup-file"
          />
          <FileLabel htmlFor="backup-file">
            Choose Backup File
          </FileLabel>

          {selectedBackup && (
            <div style={{ marginTop: theme.spacing.md }}>
              <strong>Selected Backup:</strong><br />
              Date: {formatDate(selectedBackup.timestamp)}<br />
              Version: {selectedBackup.version}<br />
              Screens: {Object.keys(selectedBackup.collections.screens).length}<br />
              Checkpoints: {Object.keys(selectedBackup.collections.checkpoints).length}
            </div>
          )}
        </Card>
      </Grid>

      {/* Progress Bar */}
      {loading && (
        <div style={{ marginTop: theme.spacing.lg }}>
          <ProgressBar>
            <ProgressFill progress={progress} />
          </ProgressBar>
          <ProgressText>{progressText}</ProgressText>
        </div>
      )}

      {/* Status Messages */}
      {status && (
        <StatusMessage type={status.type}>
          {status.message}
        </StatusMessage>
      )}

      {/* Validation Results */}
      {validationResult && (
        <div style={{ marginTop: theme.spacing.lg }}>
          <h4>Backup Validation:</h4>
          {validationResult.errors.length > 0 && (
            <StatusMessage type="error">
              <strong>Errors:</strong> {validationResult.errors.join(', ')}
            </StatusMessage>
          )}
          {validationResult.warnings.length > 0 && (
            <StatusMessage type="warning">
              <strong>Warnings:</strong> {validationResult.warnings.join(', ')}
            </StatusMessage>
          )}
          {validationResult.isValid && (
            <StatusMessage type="success">
              <strong>✓ Backup is valid and ready for restoration</strong>
            </StatusMessage>
          )}
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirm Restoration</ModalTitle>
            <ModalMessage>
              You are about to restore a backup from {formatDate(selectedBackup.timestamp)}. 
              This will affect your current funnel data.
            </ModalMessage>

            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="clear-existing"
                checked={restoreOptions.clearExisting}
                onChange={(e) => setRestoreOptions({
                  ...restoreOptions,
                  clearExisting: e.target.checked
                })}
              />
              <CheckboxLabel htmlFor="clear-existing">
                Clear existing screens and checkpoints before restoration
              </CheckboxLabel>
            </CheckboxContainer>

            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="clear-users"
                checked={restoreOptions.clearUsers}
                onChange={(e) => setRestoreOptions({
                  ...restoreOptions,
                  clearUsers: e.target.checked
                })}
              />
              <CheckboxLabel htmlFor="clear-users">
                Clear existing user data (WARNING: This will delete all user sessions)
              </CheckboxLabel>
            </CheckboxContainer>

            <ButtonGroup>
              <Button 
                variant="secondary" 
                onClick={() => setShowRestoreModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleDryRun}
                disabled={loading}
              >
                Test Restore
              </Button>
              <Button 
                variant="danger" 
                onClick={handleRestore}
                disabled={loading}
              >
                {loading ? 'Restoring...' : 'Restore Backup'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default BackupManager;
